import React, { useState } from 'react';
import { Box, Button, TextField, Typography, LinearProgress, Dialog, DialogActions, DialogContent, DialogTitle, Checkbox, FormControlLabel } from '@mui/material';
import ApiService from '../../services/api-service';  // Assuming your custom API service

const UploadVideoPopup = ({ open, onClose, studioId, onSuccess, onError }) => {
  const [title, setTitle] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [isExplicit, setIsExplicit] = useState(false);  // New state for isExplicit
  const [uploading, setUploading] = useState(false);
  const [uploadingProgress, setUploadingProgress] = useState(0);  // Progress state
  const [message, setMessage] = useState('');

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('Selected file:', file);  // Log the file details
      setVideoFile(file);
      setMessage('');
    } else {
      setMessage('Please select a valid video file.');
    }
  };

  const handleUpload = async () => {
    if (!videoFile || !title || !studioId) {
      setMessage('Please fill in all fields (studio ID, title, and video).');
      return;
    }
  
    setUploading(true);  // Start uploading state
  
    const payload = new FormData();
    payload.append("file", videoFile, videoFile.name); // Attach the file
    payload.append('title', title);
    payload.append('studioId', studioId);
    payload.append('isExplicit', isExplicit ? 'true' : 'false');  // Handle explicit content flag
  
    // Log the payload for debugging purposes
    for (const pair of payload.entries()) {
      console.log(`${pair[0]}: ${pair[1]}`);
    }
  
    try {
      // API call: studioId is the first argument, formData (payload) is the second
      const response = await ApiService.UPVID(studioId, payload, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadingProgress(percentCompleted);  // Update progress state
        },
      });
  
      console.log('Upload response:', response);
  
      // Check if response contains the custom success status
      const isSuccess = response?.status === 'success' && response?.code === 200;
  
      if (isSuccess) {
        setMessage('Video uploaded successfully!');
        if (response.result) {
          onSuccess(response.result);  // Call the success handler passed in as a prop
        } else {
          console.warn('Upload successful, but no data returned.');
        }
        onClose();  // Close the popup on success
      } else {
        // If status doesn't match, throw an error with the response
        throw new Error(`Upload failed with status: ${response?.status || 'Unknown'}`);
      }
    } catch (error) {
      console.error('Video upload failed', error);
  
      // Improved error message handling
      setMessage(error.response?.data?.message || 'Failed to upload video. Please try again.');
      onError(error);  // Call the error handler passed in as a prop
    } finally {
      setUploading(false);  // Reset uploading state
    }
  };
  

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        // Only close on escape key, not backdrop click
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      fullWidth 
      maxWidth="sm">
      <DialogTitle>Upload a Video</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Title Input */}
          <TextField
            label="Video Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />

          {/* File Input for Video */}
          <input
            type="file"
            accept="video/*"
            onChange={handleFileChange}
            style={{ marginTop: '10px', display: 'block' }}
          />

          {/* IsExplicit Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isExplicit}
                onChange={(e) => setIsExplicit(e.target.checked)}
              />
            }
            label="Is Explicit Content"
          />

          {/* Progress Bar and Upload Feedback */}
          {uploading && (
            <>
              <LinearProgress variant="determinate" value={uploadingProgress} sx={{ mt: 2 }} />
              <Typography sx={{ mt: 1 }}>Uploading: {uploadingProgress}%</Typography>
            </>
          )}
          {message && <Typography sx={{ mt: 2, color: message.includes('success') ? 'green' : 'red' }}>{message}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">Cancel</Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          color="primary"
          disabled={uploading || !videoFile}  // Disable upload button during upload
        >
          {uploading ? `Uploading (${uploadingProgress}%)...` : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadVideoPopup;
